import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Send, Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId: string;
  postTitle: string;
  /** When true, both test send and full send are disabled — save first. */
  dirty?: boolean;
  onSent?: () => void;
}

type Locale = "en" | "sv" | "all";

const SendCampaignDialog = ({ open, onOpenChange, postId, postTitle, dirty, onSent }: Props) => {
  const [counts, setCounts] = useState<{ en: number; sv: number } | null>(null);
  const [locale, setLocale] = useState<Locale>("all");
  const [subjectOverride, setSubjectOverride] = useState("");
  const [psNote, setPsNote] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubjectOverride("");
    setPsNote("");
    setLocale("all");
    setCounts(null);

    (async () => {
      // Confirmed, non-unsubscribed subscribers per locale
      const { data, error } = await supabase
        .from("newsletter_subscribers")
        .select("locale")
        .not("confirmed_at", "is", null)
        .is("unsubscribed_at", null);
      if (error) {
        toast({ title: "Couldn't load audience", description: error.message, variant: "destructive" });
        setCounts({ en: 0, sv: 0 });
        return;
      }
      const en = data.filter((r) => r.locale !== "sv").length;
      const sv = data.filter((r) => r.locale === "sv").length;
      setCounts({ en, sv });
    })();

    // Prefill test email with current admin
    supabase.auth.getUser().then(({ data }) => {
      if (data.user?.email) setTestEmail(data.user.email);
    });
  }, [open]);

  const audienceCount = counts
    ? locale === "all" ? counts.en + counts.sv : locale === "sv" ? counts.sv : counts.en
    : 0;

  const invokeSend = async (testOnly: boolean) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({ title: "Not signed in", variant: "destructive" });
      return null;
    }
    const { data, error } = await supabase.functions.invoke("send-newsletter-campaign", {
      body: {
        postId,
        locale,
        subjectOverride: subjectOverride.trim() || undefined,
        psNote: psNote.trim() || undefined,
        testEmail: testOnly ? testEmail.trim() : undefined,
      },
    });
    if (error) {
      toast({ title: "Send failed", description: error.message, variant: "destructive" });
      return null;
    }
    return data;
  };

  const handleTest = async () => {
    if (!testEmail.trim()) {
      toast({ title: "Enter a test email", variant: "destructive" });
      return;
    }
    setSendingTest(true);
    const res = await invokeSend(true);
    setSendingTest(false);
    if (res) toast({ title: "Test email queued", description: testEmail });
  };

  const handleSend = async () => {
    setSending(true);
    const res = await invokeSend(false);
    setSending(false);
    setConfirmOpen(false);
    if (res) {
      toast({
        title: "Campaign queued",
        description: `Queued for ${res.recipientCount ?? 0} subscriber(s).`,
      });
      onSent?.();
      onOpenChange(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send to subscribers</DialogTitle>
            <DialogDescription>
              Send "{postTitle}" as a newsletter issue to confirmed subscribers.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
              <div className="font-medium mb-1">Audience</div>
              {counts ? (
                <div className="text-muted-foreground">
                  English: <span className="text-foreground">{counts.en}</span> · Swedish:{" "}
                  <span className="text-foreground">{counts.sv}</span> · Total:{" "}
                  <span className="text-foreground">{counts.en + counts.sv}</span>
                </div>
              ) : (
                <div className="text-muted-foreground">Loading…</div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="locale">Send to</Label>
              <Select value={locale} onValueChange={(v) => setLocale(v as Locale)}>
                <SelectTrigger id="locale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subscribers</SelectItem>
                  <SelectItem value="en">English only</SelectItem>
                  <SelectItem value="sv">Swedish only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject (optional)</Label>
              <Input
                id="subject"
                value={subjectOverride}
                onChange={(e) => setSubjectOverride(e.target.value)}
                placeholder={postTitle}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use the post title.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ps">Personal note (optional)</Label>
              <Textarea
                id="ps"
                value={psNote}
                onChange={(e) => setPsNote(e.target.value)}
                placeholder="Appears as a P.S. line at the bottom of the email."
                rows={3}
              />
            </div>

            <div className="space-y-2 pt-2 border-t border-border">
              <Label htmlFor="test">Test send</Label>
              <div className="flex gap-2">
                <Input
                  id="test"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                />
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={sendingTest || sending || dirty}
                  title={dirty ? "Save changes before sending" : undefined}
                >
                  {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send test"}
                </Button>
              </div>
              {dirty && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  You have unsaved changes — save the post before sending.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={sending || !counts || audienceCount === 0 || dirty}
              title={dirty ? "Save changes before sending" : undefined}
            >
              <Send className="w-4 h-4 mr-2" />
              Send to {audienceCount} subscriber{audienceCount === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this newsletter?</AlertDialogTitle>
            <AlertDialogDescription>
              The email will be queued for {audienceCount} subscriber
              {audienceCount === 1 ? "" : "s"}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSend} disabled={sending}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SendCampaignDialog;
